<i18n>
[
    "recentOrderTray__reviewsCleared--title",
    "recentOrderTray__reviewsCleared--subtitle",
    "recentOrderTray__reviewsCleared--CTA",
]
</i18n>

<template>
    <div v-if="shouldRender" class="c-recent-order-tray" :style="style">
        <ProductTray
            :id="'recent-order-tray'"
            :listId="listId"
            :listName="listName"
            :contentModuleId="contentModuleId"
            :contentTitle="contentTitle"
            :products="unreviewedProducts"
            :headerLink="headerLink"
            :headerTitle="headerTitle"
            :headerSubtitle="headerSubtitle"
            :totalProducts="totalProducts"
            :mobilePeek="mobilePeek"
            :mobilePeekPercent="mobilePeekPercent"
            :mediumPeekPercent="mediumPeekPercent"
            :displayCounter="false"
            :desktopSlideSize="desktopSlideSize"
            :xlDesktopSlideSize="xlDesktopSlideSize"
            :mediumSlideSize="mediumSlideSize"
            :mobileSlideSize="mobileSlideSize"
            :translucentButtons="translucentButtons"
            :lightButtons="lightButtons"
            :showEmptyContentSlot="showEmptyContentSlot"
            :showClosetButtonOnTile="false"
            :showReviewButtonOnTile="true"
            :showProductInfo="false"
            :analyticsType="analyticsType"
            :faceOutImage="faceOutImage"
            :hasEnteredViewport="hasEnteredViewport"
            :isLoaded="true"
        >
            <template #empty-content>
                <div class="c-recent-order-tray__empty-state u-text--center">
                    <div class="o-row o-row--nested u-spacer--3">
                        <div class="o-extra-small--12">
                            <h3 class="u-spacer--1">
                                {{ $t('recentOrderTray__reviewsCleared--title') }}
                            </h3>
                            <p class="u-text--gray u-spacer--0pt5">
                                {{ $t('recentOrderTray__reviewsCleared--subtitle') }}
                            </p>
                            <BaseLink :to="rentalHistoryLink" class="o-text--link o-text--caption">
                                {{ $t('recentOrderTray__reviewsCleared--CTA') }}
                            </BaseLink>
                        </div>
                    </div>
                    <div class="o-row o-row--nested">
                        <div class="o-extra-small--12">
                            <Illustration
                                :illustrationSrc="IllustrationImage"
                                illustrationSize="128"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </ProductTray>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { filter } from 'lodash-es';

import { useActions, useState } from '~coreModules/core/js/composables/vuexHelpers';

import { BOX_ORDER_STATUSES } from '~modules/box/js/box-constants';
import { rentalRouteNames } from '~modules/core/js/router-constants';
import { CAROUSEL_IDS } from '~modules/analytics/js/analytics-constants';

import { BOX_MODULE_NAME, FETCH_BOX_DATA } from '~modules/box/js/box-store';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';
import ProductTray from '~modules/core/components/ui/ProductTray.vue';
import Illustration from '~coreModules/core/components/ui/Illustration.vue';

import IllustrationImage from '~images/illustration__sizes.png';

const props = defineProps({
    ...productTrayProps,
    maxProducts: {
        type: Number,
        default: 15,
    },
    paddingBottom: {
        type: String,
        default: null,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});

const rentalHistoryLink = { name: rentalRouteNames.account.history };
const analyticsType = CAROUSEL_IDS.RECENT_ORDER_TRAY;

const {
    recentlyOrderedProducts,
    hasReviewedAllRecentlyOrdered,
    boxOrderStatus,
} = useState(BOX_MODULE_NAME, {
    recentlyOrderedProducts: state => state.recentOrder?.products,
    hasReviewedAllRecentlyOrdered: state => state.recentOrder?.hasReviewedAllRecentlyOrdered,
    boxOrderStatus: state => state.boxOrderStatus,
});

const shouldRender = computed(() => {
    const { DELIVERED, RETURN_STARTED } = BOX_ORDER_STATUSES;
    return [DELIVERED, RETURN_STARTED].includes(boxOrderStatus.value);
});

const showEmptyContentSlot = computed(() => recentlyOrderedProducts.value.length &&
    hasReviewedAllRecentlyOrdered.value);

const unreviewedProducts = computed(() => filter(recentlyOrderedProducts.value, product => !product.overallRating));

const totalProducts = computed(() => unreviewedProducts.value.length);

const style = computed(() => (props.paddingBottom ? { paddingBottom: props.paddingBottom } : {}));

const { fetchBoxData } = useActions(BOX_MODULE_NAME, { fetchBoxData: FETCH_BOX_DATA });

watch(() => props.hasEnteredViewport, (newValue, oldValue) => {
    if (shouldRender.value && !oldValue && newValue) {
        fetchBoxData();
    }
}, { immediate: true });
</script>

<style lang="scss">

</style>
